import { RWSService } from "@rws-framework/client";

import { HTTPClient, HTTPError } from "src/services/httpClient";
import { allPlugins, allowedPlugins, collectOfferings, getAllowedPlugin, offeredComponents } from "./registry";
import type {
  PluginComponentOption,
  PluginDefinition,
  PluginOfferings,
  PluginSubmissionResult,
  PluginStudentSubmissionResponse,
  PluginSubmissionList,
  PluginGradePayload,
  PluginSubmissionRecord,
  ResellerPluginConfig
} from "./types";


class PluginService extends RWSService {
  static _IN_CLIENT: boolean = true;
  private client = new HTTPClient({ formatError: PluginError.fromResponse });

  listOfferedComponents(): Promise<PluginOfferings> {
    return offeredComponents();
  }

  /** Lists components allowed by reseller configuration. Missing/empty configuration means no components. */
  listOfferedComponentForReseller(config?: ResellerPluginConfig): Promise<PluginOfferings> {
    return collectOfferings(allowedPlugins(config));
  }

  listPlugins(): PluginDefinition[] {
    return allPlugins();
  }

  /** Plugin getter respecting reseller configuration. */
  getPlugin(subType: string | undefined | null, config?: ResellerPluginConfig): PluginDefinition | null {
    return getAllowedPlugin(subType, config);
  }

  // submissions
  submit(courseId: string, componentId: string, payload: unknown): Promise<PluginSubmissionResult> {
    return this.client.post(`/submissions/${courseId}/plugin_component/${componentId}`, {
      body: { payload },
      csrf: true,
    });
  }

  // submissions:student
  getSubmission(
    courseId: string,
    componentId: string,
    studentId: string,
  ): Promise<PluginSubmissionResult | null> {
    return this.client
      .get(`/submissions/${courseId}/plugin_component/${componentId}/${studentId}`)
      .then(({ submission }: PluginStudentSubmissionResponse) => submission);
  }

  // submissions
  listSubmissions(courseId: string, componentId: string): Promise<PluginSubmissionList> {
    return this.client.get(`/submissions/${courseId}/plugin_component/${componentId}`);
  }

  // gradebook:add
  saveGrade(courseId: string, studentId: string, payload: PluginGradePayload) {
    return this.client.put(`/gradebook/${courseId}/${studentId}`, {
      body: payload,
      csrf: true,
    });
  }
}

export class PluginError extends HTTPError {
  get humanMessage() {
    switch (this.errorCode) {
      case "plugin-not-found":
      case "plugin-unknown-sub-type":
        return "This activity is no longer available.";

      case "plugin-invalid-payload":
        return "Your response could not be submitted.";

      default:
        // sub_types return their own errno with a user-facing message
        return super.humanMessage;
    }
  }

  static fromResponse(res: Response, errorCode: string = "unknown") {
    return new PluginError(res.statusText, errorCode);
  }
}

export default PluginService.getSingleton();
export { PluginService as PluginServiceInstance };
export type {
  PluginComponentOption,
  PluginDefinition,
  PluginOfferings,
  PluginSubmissionResult,
  PluginStudentSubmissionResponse,
  PluginSubmissionList,
  PluginGradePayload,
  PluginSubmissionRecord,
  ResellerPluginConfig
};